import React, { useEffect, useState, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { HeroUIProvider } from '@heroui/react';
import { HelmetProvider } from 'react-helmet-async';

import { TransitionProvider } from './components/TransitionProvider';
import { PreloaderContext } from './context/PreloaderContext';
import CookieConsent from './components/CookieConsent';
import { trackPageview } from './lib/analytics';
import Preloader from './components/Preloader';
// Landing page stays eager so the first paint never waits on a second chunk;
// every other route is split so its libraries (three, matter-js, pageflip, …)
// load only when that page is visited.
import HomePage from './components/HomePage';
import { useLenis } from './hooks/useLenis';

import './index.css';

const AboutPage = lazy(() => import('./components/AboutPage'));
const ContactPage = lazy(() => import('./components/ContactPage'));
const WorksPage = lazy(() => import('./components/WorksPage'));
const ServicesPage = lazy(() => import('./components/ServicesPage'));
const TermsPage = lazy(() => import('./components/TermsPage'));
const BrandGuidelinesPage = lazy(() => import('./components/BrandGuidelinesPage'));
const AdminRestartPage = lazy(() => import('./components/AdminRestartPage'));
const ProjectPage = lazy(() => import('./components/ProjectPage'));
const AnimationGallery = lazy(() => import('./components/AnimationGallery'));
const InsightsPage = lazy(() => import('./components/InsightsPage'));
const InsightPage = lazy(() => import('./components/InsightPage'));
const ToolsPage = lazy(() => import('./components/ToolsPage'));

function ScrollToTop() {
  const { pathname, search } = useLocation();

  useEffect(() => {
    const scrollToTop = () => {
      const lenis = typeof window !== 'undefined' ? window.__lenis : null;
      if (lenis?.scrollTo) {
        lenis.scrollTo(0, { immediate: true, lock: true });
      } else {
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      }
    };

    // Small delay to ensure Lenis is ready
    const timeoutId = setTimeout(scrollToTop, 0);
    return () => clearTimeout(timeoutId);
  }, [pathname, search]);

  return null;
}

function LenisScroller() {
  useLenis();
  return null;
}

function RouteScopedLoader({ onComplete }) {
  const [shown, setShown] = useState(false);

  useEffect(() => {
    // Only show preloader on first load (check sessionStorage)
    const hasLoadedBefore = sessionStorage.getItem('hasLoadedBefore');
    
    if (!hasLoadedBefore) {
      setShown(true);
    } else {
      onComplete?.();
    }
  }, [onComplete]);

  if (!shown) return null;
  return <Preloader onComplete={onComplete} />;
}

function AnalyticsTracker() {
  const { pathname, search } = useLocation();
  useEffect(() => {
    trackPageview(`${pathname}${search}`);
  }, [pathname, search]);
  return null;
}

function AppContent() {
  const location = useLocation();
  const [loaderComplete, setLoaderComplete] = useState(false);

  const handleLoaderComplete = () => {
    // Mark the intro as seen for the rest of the session, in one place.
    try {
      sessionStorage.setItem('hasLoadedBefore', 'true');
    } catch (_) {
      // sessionStorage can be unavailable (private mode / SSR) — ignore.
    }
    setLoaderComplete(true);
  };

  return (
    <HeroUIProvider>
      <PreloaderContext.Provider value={loaderComplete}>
        <TransitionProvider>
          <LenisScroller />
          <ScrollToTop />
          <AnalyticsTracker />
          {!loaderComplete && (
            <RouteScopedLoader onComplete={handleLoaderComplete} />
          )}
          <div className="App">
            <AnimatePresence mode="wait">
              <Suspense fallback={null}>
                <Routes location={location} key={`${location.pathname}${location.search}`}>
                  <Route path="/" element={<HomePage />} />
                  <Route path="/about" element={<AboutPage />} />
                  <Route path="/contact" element={<ContactPage />} />
                  <Route path="/works" element={<WorksPage />} />
                  <Route path="/services" element={<ServicesPage />} />
                  <Route path="/terms" element={<TermsPage />} />
                  <Route path="/brand-guidelines" element={<BrandGuidelinesPage />} />
                  <Route path="/admin/restart-calc" element={<AdminRestartPage />} />
                  <Route path="/project/:id" element={<ProjectPage />} />
                  <Route path="/project" element={<ProjectPage />} />
                  <Route path="/animations" element={<AnimationGallery />} />
                  <Route path="/insights" element={<InsightsPage />} />
                  <Route path="/insights/:slug" element={<InsightPage />} />
                  <Route path="/tools" element={<ToolsPage />} />
                </Routes>
              </Suspense>
            </AnimatePresence>
          </div>
          <CookieConsent />
        </TransitionProvider>
      </PreloaderContext.Provider>
    </HeroUIProvider>
  );
}

function App() {
  return (
    <HelmetProvider>
      <Router>
        <AppContent />
      </Router>
    </HelmetProvider>
  );
}

export default App;
